import { useEffect, useMemo, useState } from "react";
import PricingCard from "./PricingCard";
import { pricingData } from "./pricingData";
import { getAllPublicPlans } from "../../../utils/APIs";
import Loader from "../../../components/UI/Loader/Loader";
import { useUserContext } from "../../../contexts/UserContext";
import { permission } from "../../../config/permission";
import { SegmentedControl } from "../../../components/UI";

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState("monthly"); // monthly | yearly
  const [planView, setPlanView] = useState("BASE"); // BASE | TOPUP
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);

  const { checkPermission } = useUserContext();

  // 🔐 Permission gate for top-ups
  const canBuyTopups =
    checkPermission(permission.SHOW_AI_GENERATE_VARIANTS) ||
    checkPermission(permission.SHOW_AI_GENERATE_FIELDS);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await getAllPublicPlans();

      if (res.status && Array.isArray(res.data)) {
        const mergedPlans = res.data.map((apiPlan) => {
          const staticPlan = pricingData.find((p) =>
            apiPlan.plan_name.toLowerCase().includes(p.name.toLowerCase())
          );

          return {
            ...apiPlan,
            features: staticPlan?.features || [],
            buttonText: staticPlan?.buttonText,
            price_currency: apiPlan.price_currency
          }
        });

        setPlans(mergedPlans);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // 🔒 Safety: if permission is lost while on Top-ups
  useEffect(() => {
    if (planView === "TOPUP" && !canBuyTopups) {
      setPlanView("BASE");
      setBillingCycle("monthly");
    }
  }, [planView, canBuyTopups]);

  // ✅ Correct filtering
  const filteredPlans = useMemo(() => {
    if (!plans.length) return [];

    if (planView === "TOPUP") {
      if (!canBuyTopups) return [];
      return plans.filter((plan) => plan.is_topup === true);
    }

    const durationInDays = billingCycle === "monthly" ? 30 : 365;
    return plans.filter(
      (plan) => !plan.is_topup && plan.duration === durationInDays
    );
  }, [plans, billingCycle, planView, canBuyTopups]);

  return (
    <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
      {loading && <Loader />}

      <div className="max-w-7xl mx-auto">
        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <SegmentedControl
            value={planView === 'TOPUP' ? 'topups' : billingCycle}
            onChange={(val) => {
              if (val === 'topups') {
                setPlanView('TOPUP');
              } else {
                setPlanView('BASE');
                setBillingCycle(val);
              }
            }}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'yearly', label: 'Yearly', id: 'billing-cycle-tab-yearly' },
              ...(canBuyTopups ? [{ value: 'topups', label: 'Top-ups', id: 'billing-cycle-tab-topups' }] : []),
            ]}
          />
        </div>

        {/* Savings Banner (Base plans only) */}
        {planView === "BASE" && (
          <div
            className="mb-12 border-l-4 p-2"
            style={{ backgroundColor: "#EEF2F9", borderColor: "#465DAE" }}
          >
            <p className="font-medium text-center" style={{ color: "#465DAE" }}>
              Want to save 20%? Just toggle to yearly - it's that easy.
            </p>
          </div>
        )}

        {/* Pricing Cards Grid */}
        <div className="flex flex-wrap justify-center gap-6">
          {filteredPlans.length === 0 ? (
            <p className="text-gray-500">No plans available.</p>
          ) : (
            filteredPlans.map((plan, index) => (
              <div key={index} className="w-full sm:w-80 md:w-96">
                <PricingCard
                  plan={plan}
                  index={index}
                  billingCycle={billingCycle}
                  isEnterprise={plan.plan_name
                    .toLowerCase()
                    .includes("enterprise")}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}